import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, styled, Card, Breadcrumbs, Link } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import awsImage from "../assets/images/light/aws-image.png";
import eksImage from "../assets/images/light/eks-image.png";
import { Link as RouterLink } from 'react-router-dom';

// Header Container
const HeaderContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  marginBottom: '16px',
  position: 'relative',
});

// Back Button
const BackButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  color: '#1674B4',
  fontFamily: 'Proxima Nova',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  position: 'absolute',
  right: '910px',
  top: '50%',
  transform: 'translateY(-50%)',
  '&:hover': {
    textDecoration: 'none',
  },
  '&::before': {
    content: '"<"',
    marginRight: '4px',
  },
});

// Breadcrumbs Container
const BreadcrumbsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'Proxima Nova',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  position: 'absolute',
  right: '-28px',
  top: '50%',
  transform: 'translateY(-50%)',
  '& > *:not(:last-child)::after': {
    content: '" > "',
    color: '#6D6D6D',
    margin: '0 6px',
  },
});

// Breadcrumb Item
const BreadcrumbItem = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  fontFamily: 'Proxima Nova',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  '& a': {
    color: '#1674B4',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  '&:last-child': {
    '& a': {
      color: '#6D6D6D',
      cursor: 'default',
      '&:hover': {
        textDecoration: 'none',
      },
    },
  },
});

const BreadcrumbLink = styled(Link)({
  color: '#1674B4',
  textDecoration: 'none',
  fontSize: '14px',
  fontFamily: 'Proxima Nova',
  '&:hover': {
    textDecoration: 'none',
  },
  '&:last-child': {
    color: '#6D6D6D',
    textDecoration: 'none',
    cursor: 'default',
  },
});

// Styled Components for the Dashboard Container
const DashboardContainer = styled(Box)({
  minHeight: "90vh",
  backgroundColor: "#f0f2f5",
  paddingTop: "20px", // Increased from 10px to 40px to move content down
  display: "flex",
  flexDirection: "column",
  alignItems: "center", // Center content horizontally
  overflow: "hidden",
  position: "relative",
});

// Content Wrapper with Max Width and Padding
const ContentWrapper = styled(Box)({
  width: "100%",
  maxWidth: "1000px",
  display: "flex",
  flexDirection: "column",
  margin: "0 auto 60px", // Center the content and add bottom margin
  padding: "0 24px", // Add horizontal padding
});

// Page Title Styling
const PageTitle = styled(Typography)({
  color: "#1674B4",
  fontFamily: "Proxima Nova",
  fontSize: "30px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "normal",
  textAlign: "left",
  marginBottom: "16px",
});

// Divider Styling
const DividerLine = styled('div')({
  width: '982px',
  height: '1px',
  background: '#B1B1B1',
  marginBottom: '16px',
});

// Description Styling
const Description = styled(Typography)({
  color: "#2E2E2E",
  fontFamily: "Proxima Nova",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "normal",
  marginBottom: "32px",
  maxWidth: "800px",
  '& strong': {
    fontWeight: 700,
  }
});

// Cards Container Styling
const CardsContainer = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(3, 308px)",
  gap: "24px",
  marginBottom: "50px",
  justifyContent: "space-between",
  width: "100%",
});

// Card Styling
const StyledCard = styled(Card)({
  width: "308px",
  height: "138px",
  flexShrink: 0,
  borderRadius: "8px",
  background: "#FFF",
  boxShadow: "2px 2px 2px 0px rgba(0, 0, 0, 0.25)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  padding: "16px",
  boxSizing: "border-box",
  '&:hover': {
    boxShadow: "4px 4px 8px 0px rgba(0, 0, 0, 0.2)",
  },
});

// Card Image Styling
const CardImage = styled('img', {
  shouldForwardProp: (prop) => prop !== 'isEks',
})(({ isEks }) => ({
  marginBottom: "12px",
  ...(isEks ? {
    width: "53.191px",
    height: "59.785px",
    aspectRatio: "53.19/59.79"
  } : {
    width: "99.295px",
    height: "60px",
    aspectRatio: "99.29/60.00"
  })
}));

// Card Text Styling
const CardText = styled(Typography)({
  color: "#1674B4",
  textAlign: "center",
  fontFamily: "Proxima Nova",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "normal",
  whiteSpace: "pre-line",
});



function AwsAccounts() {
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Get account IDs and cluster name from environment variables
  const primaryAccountId = process.env.REACT_APP_AWS_ACCOUNT_ID_1;
  const secondaryAccountId = process.env.REACT_APP_AWS_ACCOUNT_ID_2;
  const eksClusterName = process.env.REACT_APP_EKS_CLUSTER_NAME;
  const orgName = process.env.REACT_APP_ORG_NAME;

  const cards = [
    {
      id: 1,
      title: `AWS ${orgName}\nAccount - 1 - Cost`,
      image: awsImage,
      onClick: () => navigate(`/aws-cost-dashboard/${primaryAccountId}`)
    },
    {
      id: 2,
      title: `AWS ${orgName}\nAccount - 1 - EKS`,
      image: eksImage,
      onClick: () => navigate(`/eks-monitoring/${primaryAccountId}?cluster=${eksClusterName}`)
    },
    {
      id: 3,
      title: `AWS ${orgName}\nAccount - 2 - Cost`,
      image: awsImage,
      onClick: () => navigate(`/aws-cost-dashboard/${secondaryAccountId}`)
    }
  ];

  const handleBack = () => {
    navigate(-1); // Go back to the previous page
  };

  return (
    <DashboardContainer>
      <ContentWrapper>
        <HeaderContainer>
          <BackButton onClick={handleBack}>
            Back
          </BackButton>
          <BreadcrumbsContainer>
            <BreadcrumbItem>
              <Link component={RouterLink} to="/home">
                Dashboard
              </Link>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <Link component={RouterLink} to="/monitoring-dashboard">
                Monitoring
              </Link>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <Link>
                AWS Monitoring
              </Link>
            </BreadcrumbItem>
          </BreadcrumbsContainer>
        </HeaderContainer>
        <PageTitle>AWS Monitoring</PageTitle>
        
        <DividerLine />
        
        <Description component="div">
          <strong>Select an AWS account to explore cost, performance, and resource usage.</strong>
          <br />
          Monitor multiple accounts independently to gain deeper operational insights.
        </Description>

        {/* Cards Section */}
        <CardsContainer>
          {cards.map((card) => (
            <StyledCard key={card.id} onClick={card.onClick}>
              <CardImage 
                src={card.image} 
                alt={card.title.split('\n')[0]} 
                isEks={card.id === 2} 
              />
              <CardText>{card.title}</CardText>
            </StyledCard>
          ))}
        </CardsContainer>
      </ContentWrapper>


    </DashboardContainer>
  );
}

export default AwsAccounts;
